/**
 * Artboard 10 · Datos de facturación — shown to a cashier in bad light: QR for whoever scans, big text for whoever types.
 * The card is the caller's legal entity (3 Sep 2026); a line names it and lets the employee change it (new reports only).
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useNavigate } from 'react-router-dom';
import { ApiError, call } from '../api/client.ts';
import { useLoad } from '../state/hooks.ts';
import { Button, ErrorNote, Icon, Loading, Sheet, TopBar, useToast } from '../theme/ui.tsx';
import { EntityChooser } from './Inicio.tsx';

export default function CodigoFiscal() {
  const nav = useNavigate();
  const card = useLoad(() => call('entity.fiscalCard'), []);
  const [svg, setSvg] = useState<string>(''); const [bright, setBright] = useState(false); const [changing, setChanging] = useState(false);
  const [toast, showToast] = useToast();
  useEffect(() => { if (card.data) QRCode.toString(card.data.qrPayload, { type: 'svg', margin: 0, errorCorrectionLevel: 'M', color: { dark: '#1a1815', light: '#ffffff' } }).then(setSvg).catch(() => setSvg('')); }, [card.data]);
  if (card.loading) return <div className="screen screen--white"><Loading /></div>;
  const chooser = (title: string, lead: string) => (
    <div className="pad" style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 }}>
      <div><div className="h3">{title}</div><div className="lead" style={{ marginTop: 8 }}>{lead}</div></div>
      <EntityChooser current={card.data?.entityId ?? null} cta="Guardar" onChosen={(e) => { setChanging(false); card.reload(); showToast(`Listo. Tus nuevos reportes saldrán a nombre de ${e.name}.`); }} />
    </div>
  );
  if (card.error instanceof ApiError && card.error.entityRequired) {
    return <div className="screen screen--white"><div className="phone"><TopBar close={() => nav(-1)} title="Datos de facturación" muted={false} />{chooser('¿En qué empresa estás?', 'Elige la que paga tu nómina; la factura debe salir a su nombre.')}</div>{toast}</div>;
  }
  if (card.error || !card.data) return <div className="screen screen--white"><ErrorNote error={card.error ?? new Error('Sin datos')} retry={card.reload} /></div>;
  const c = card.data;
  const copy = async () => { try { await navigator.clipboard.writeText(c.qrPayload); showToast('Copiado. Pégalo donde lo pidan.'); } catch { showToast('No se pudo copiar en este navegador.'); } };
  const K = ({ k, v, mono, size = 19 }: { k: string; v: string; mono?: boolean; size?: number }) => <div><div className="label label--xs">{k}</div><div className={mono ? 'mono' : ''} style={{ fontSize: size, fontWeight: mono ? 500 : 600, marginTop: 3, letterSpacing: mono ? '0.01em' : '-0.01em', overflowWrap: 'anywhere' }}>{v}</div></div>;
  return (
    <div className={`screen screen--white ${bright ? 'bright' : ''}`}>
      <div className="phone">
        <TopBar close={() => nav(-1)} title="Datos de facturación" muted={false} />
        {!bright && <div className="pad sub" style={{ fontSize: 14, padding: '12px 20px 16px', lineHeight: 1.45 }}>Muéstralo en la caja. Que la factura salga a nombre de la empresa, no al tuyo.</div>}
        <div style={{ padding: `${bright ? 24 : 0}px 20px 20px`, display: 'flex', justifyContent: 'center' }}>
          <div className="qr-box" style={bright ? { width: 300, height: 300 } : undefined}>{svg ? <div style={{ width: bright ? 260 : 204, height: bright ? 260 : 204 }} dangerouslySetInnerHTML={{ __html: svg }} /> : <Icon.Qr size={60} color="var(--ink-5)" />}</div>
        </div>
        <div className="pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <K k="RFC" v={c.rfc} mono size={bright ? 32 : 27} />
          <K k="Razón social" v={c.razonSocial} />
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}><K k="C.P." v={c.codigoPostal} mono /><K k="Régimen" v={c.regimenFiscal} mono /><K k="Uso" v={c.usoCfdi} mono /></div>
          {c.domicilio && !bright && <K k="Domicilio" v={c.domicilio} size={14} />}
          <K k="Enviar la factura a" v={c.deliveryEmail} mono size={16} />
          {!bright && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingTop: 6, borderTop: '1px solid var(--line-2)' }}>
              <div className="sub" style={{ lineHeight: 1.4 }}>Tu empresa. Los reportes nuevos salen a su nombre.</div>
              <Button variant="ghost" sm inline onClick={() => setChanging(true)}>Cambiar</Button>
            </div>
          )}
        </div>
        <div className="footer footer--row">
          <Button variant="secondary" onClick={copy} icon={<Icon.Copy size={17} width={1.9} />}>Copiar todo</Button>
          <Button variant="secondary" onClick={() => setBright((b) => !b)} icon={<Icon.Sun size={17} width={1.9} />}>{bright ? 'Vista normal' : 'Subir brillo'}</Button>
        </div>
      </div>
      {changing && <Sheet onClose={() => setChanging(false)}>{chooser('Cambiar de empresa', 'Solo afecta los reportes que crees a partir de ahora; los existentes se quedan donde están.')}</Sheet>}
      {toast}
    </div>
  );
}
